import React from 'react';
import {
  filterFieldUsesOptions,
  findFilterTreeField
} from '../libs/listViewPresentation';
import type {
  ActiveFilter,
  FilterTreeField,
  FilterTreeGroup,
  FilterTreeItem
} from '../listview/types';

/* The state of the list view's filter editor: which level of the filter tree
 * is shown, which field is being filtered on, and the values picked for it.
 * Adding a filter and editing one are the same flow — editing starts on the
 * chip's field with its values filled in, and confirming updates that filter
 * instead of adding one. */

export interface ListViewFilterEditor {
  isOpen: boolean;
  /* The tree items of the level currently shown */
  items: FilterTreeItem[];
  /* The groups drilled into so far; the last one names the shown level */
  navStack: FilterTreeGroup[];
  selectedField: FilterTreeField | null;
  checkedValues: string[];
  textValue: string;
  /* Whether confirming updates an existing filter rather than adding one */
  isEditing: boolean;
  /* Whether the selected field is filtered by picking values or by typing a term */
  usesOptions: boolean;
  canConfirm: boolean;
  /* Opens the editor at the top of the tree, or closes and resets it */
  toggle: () => void;
  goBack: () => void;
  selectItem: (item: FilterTreeItem) => void;
  toggleValue: (option: string) => void;
  changeText: (value: string) => void;
  /* Opens the editor on an active filter's field, prefilled with its values */
  editFilter: (filter: ActiveFilter) => void;
  confirm: () => void;
}

interface EditorState {
  isOpen: boolean;
  navStack: FilterTreeGroup[];
  selectedField: FilterTreeField | null;
  checkedValues: string[];
  textValue: string;
  /* The id of the active filter being edited; null while adding one */
  editingFilterID: string | null;
}

const closedState: EditorState = {
  isOpen: false,
  navStack: [],
  selectedField: null,
  checkedValues: [],
  textValue: '',
  editingFilterID: null
};

const useListViewFilterEditor = (
  filterTree: FilterTreeItem[],
  onAddFilter: (filter: ActiveFilter) => void,
  onUpdateFilter: (filter: ActiveFilter) => void
): ListViewFilterEditor => {
  const [state, setState] = React.useState<EditorState>(closedState);
  const { isOpen, navStack, selectedField, checkedValues, textValue, editingFilterID } =
    state;

  const usesOptions = selectedField ? filterFieldUsesOptions(selectedField) : false;

  const toggle = () =>
    setState((current) => ({ ...closedState, isOpen: !current.isOpen }));

  const close = () => setState(closedState);

  const editFilter = (filter: ActiveFilter) => {
    /* A field that is gone from the tree is rebuilt from the filter itself, so
       an existing filter stays editable */
    const field: FilterTreeField = findFilterTreeField(filterTree, filter) || {
      kind: 'field',
      category: filter.category,
      name: filter.fieldName,
      keyword: filter.fieldKeyword,
      fieldType: filter.fieldType,
      options: filter.options
    };
    const fieldUsesOptions = filterFieldUsesOptions(field);
    setState({
      isOpen: true,
      navStack: [],
      selectedField: field,
      checkedValues: fieldUsesOptions ? filter.values : [],
      textValue: fieldUsesOptions ? '' : filter.values[0] || '',
      editingFilterID: filter.id
    });
  };

  const goBack = () =>
    setState((current) => {
      /* Backing out of the field also leaves editing mode: picking another
         field then adds a new filter instead of replacing the edited one */
      if (current.selectedField)
        return {
          ...current,
          selectedField: null,
          checkedValues: [],
          textValue: '',
          editingFilterID: null
        };
      return { ...current, navStack: current.navStack.slice(0, -1) };
    });

  const selectItem = (item: FilterTreeItem) =>
    setState((current) =>
      item.kind === 'group'
        ? { ...current, navStack: [...current.navStack, item] }
        : { ...current, selectedField: item, checkedValues: [], textValue: '' }
    );

  const toggleValue = (option: string) =>
    setState((current) => ({
      ...current,
      checkedValues: current.checkedValues.includes(option)
        ? current.checkedValues.filter((value) => value !== option)
        : [...current.checkedValues, option]
    }));

  const changeText = (value: string) =>
    setState((current) => ({ ...current, textValue: value }));

  const confirm = () => {
    if (!selectedField) return;
    const filter: ActiveFilter = {
      id: selectedField.keyword,
      category: selectedField.category,
      fieldKeyword: selectedField.keyword,
      fieldName: selectedField.name,
      fieldType: selectedField.fieldType,
      options: selectedField.options,
      values: usesOptions
        ? checkedValues
        : [textValue.trim()].filter((value) => value !== '')
    };
    if (editingFilterID) onUpdateFilter(filter);
    else onAddFilter(filter);
    close();
  };

  return {
    isOpen,
    items: navStack.length === 0 ? filterTree : navStack[navStack.length - 1].children,
    navStack,
    selectedField,
    checkedValues,
    textValue,
    isEditing: editingFilterID !== null,
    usesOptions,
    canConfirm: usesOptions ? checkedValues.length > 0 : textValue.trim() !== '',
    toggle,
    goBack,
    selectItem,
    toggleValue,
    changeText,
    editFilter,
    confirm
  };
};

export default useListViewFilterEditor;
